import axios, { AxiosResponse, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import config from "../config";
import { formatApiResponse, IGlobalResponse } from '../utils/apiResponseFormatter';
import { useAuthStore } from 'stores/useAuthStore';

const { api } = config;

const axiosNoAuth = axios.create({
  baseURL: api.API_URL || "",
  headers: { "Content-Type": "application/json" },
  maxRedirects: 5, // Suivre les redirections automatiquement
  validateStatus: (status) => status < 500, // Ne pas rejeter les redirections
});

const axiosAuth = axios.create({
  baseURL: api.API_URL || "",
  headers: { "Content-Type": "application/json" },
  maxRedirects: 5, // Suivre les redirections automatiquement
  validateStatus: (status) => status < 500, // Ne pas rejeter les redirections
});

// Intercepteur pour ajouter automatiquement le Bearer token à toutes les requêtes authentifiées
// Utilise directement le store zustand pour récupérer le token (approche peya exploitation)
axiosAuth.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Récupérer le token directement depuis le store zustand
    const token = useAuthStore.getState().user?.token;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (!token && config.url && !config.url.includes('/auth/login')) {
      // Log uniquement pour les requêtes authentifiées (pas pour le login)
      console.warn("Aucun token trouvé dans le store pour la requête:", config.url);
    }
    return config;
  },
  error => Promise.reject(error)
);

// Content type - Le token sera géré dynamiquement via l'intercepteur axiosAuth
// Plus besoin de charger depuis sessionStorage ici

// intercepting to capture errors
axiosNoAuth.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let message;
    switch (error.status) {
      case 500:
        message = "Internal Server Error";
        break;
      case 401:
        message = "Invalid credentials";
        break;
      case 404:
        message = "Sorry! the data you are looking for could not be found";
        break;
      default:
        message = error.message || error;
    }
    return Promise.reject(message);
  }
);

// Ajouter aussi l'intercepteur de réponse à axiosAuth pour la gestion d'erreurs
axiosAuth.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  function (error) {
    let message;
    const status = error.response?.status;
    
    // Gérer les erreurs de redirection (308)
    if (status === 308 || status === 301 || status === 302) {
      const location = error.response?.headers?.location;
      if (location) {
        console.warn("Redirection détectée vers:", location);
        // Ne pas rejeter, laisser axios suivre la redirection automatiquement
        return Promise.reject(error);
      }
    }
    
    switch (status) {
      case 403:
        message = "Accès interdit. Vérifiez vos permissions.";
        console.error("Erreur 403 - Token peut-être invalide ou expiré:", error.response?.data);
        break;
      case 500:
        message = "Internal Server Error";
        break;
      case 401:
        message = "Invalid credentials";
        break;
      case 404:
        message = "Sorry! the data you are looking for could not be found";
        break;
      default:
        message = error.response?.data?.message || error.message || error;
    }
    return Promise.reject(message);
  }
);

/**
 * APIClient - Utilise maintenant axiosAuth pour toutes les requêtes authentifiées
 * Le Bearer token est automatiquement ajouté via l'intercepteur dans axiosAuth
 */
class APIClient {
  /**
   * Fetches data from the given URL
   */
  get = async (url: string, use_auth: boolean = true, params?: any): Promise<IGlobalResponse<any>> => {
    let axiosResponse: Promise<AxiosResponse>;

    let paramKeys: string[] = [];

    if (params) {
      Object.keys(params).map(key => {
        paramKeys.push(key + '=' + params[key]);
        return paramKeys;
      });

      const queryString = paramKeys && paramKeys.length ? paramKeys.join('&') : "";
      axiosResponse = use_auth ? axiosAuth.get(`${url}?${queryString}`, params) : axiosNoAuth.get(`${url}?${queryString}`, params);
    } else {
      axiosResponse = use_auth ? axiosAuth.get(`${url}`, params) : axiosNoAuth.get(`${url}`, params);
    }

    const response = await axiosResponse;
    return formatApiResponse(response);
  };

  /**
   * Posts the given data to the URL
   */
  post = async (url: string, use_auth: boolean = true, data: any): Promise<IGlobalResponse<any>> => {
    const axiosResponse = use_auth ? axiosAuth.post(url, data) : axiosNoAuth.post(url, data);
    const response = await axiosResponse;
    console.log(" brut response : ", response);
    return formatApiResponse(response);    
  };

  /**
   * Updates data
   */
  update = async (url: string, use_auth: boolean = true, data: any): Promise<IGlobalResponse<any>> => {
    const axiosResponse = use_auth ? axiosAuth.patch(url, data) : axiosNoAuth.patch(url, data);
    const response = await axiosResponse;
    return formatApiResponse(response);
  };

  put = async (url: string, use_auth: boolean = true, data: any): Promise<IGlobalResponse<any>> => {
    const axiosResponse = use_auth ? axiosAuth.put(url, data) : axiosNoAuth.put(url, data);
    const response = await axiosResponse;
    return formatApiResponse(response);
  };

  /**
   * Deletes data
   */
  delete = async (url: string, use_auth: boolean = true, config?: AxiosRequestConfig): Promise<IGlobalResponse<any>> => {
    const axiosResponse = use_auth ? axiosAuth.delete(url, { ...config }) : axiosNoAuth.delete(url, { ...config });
    const response = await axiosResponse;
    return formatApiResponse(response);
  };
} 

/**
 * Récupère l'utilisateur connecté depuis le sessionStorage (stocké par zustand persist)
 * La session est perdue à la fermeture de l'onglet/navigateur.
 *
 * @returns L'utilisateur connecté ou null si aucun utilisateur n'est connecté
 */
export const getLoggedinUser = (): any => {
  try {
    const raw = sessionStorage.getItem("authUser");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.state?.user ?? parsed?.user ?? null;
  } catch {
    return null;
  }
};

export { APIClient };